import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import type { IDomEditor, IEditorConfig, IToolbarConfig } from '@wangeditor/editor';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import {
  Button,
  Cascader,
  Checkbox,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useSnapshot } from 'valtio';
import { getCategoryTreeApi } from '@/api/pointCenter/category';
import {
  addInstanceApi,
  deleteInstanceApi,
  getInstanceDetailApi,
  getInstanceListApi,
  getInstanceStatusEnumApi,
  publishInstanceApi,
  unpublishInstanceApi,
  updateInstanceApi,
} from '@/api/pointCenter/exchangeInstance';
import { getExchangeProductDetailApi, getExchangeProductPageApi } from '@/api/pointCenter/exchangeProduct';
import { getCouponDetailByCouponIdApi, queryCouponListApi } from '@/api/pointCenter/storeCoupon';
import ImageUploader from '@/component/ImageUploader';
import { state } from '@/store';
import '@wangeditor/editor/dist/css/style.css';
import { EXCHANGE_KEY } from '@/router/Permissions';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import { checkPermissions } from '@/utils/utils';

const { Text, Link } = Typography;

const instanceTypeOptions = [
  { label: '实物', value: 'PRODUCT' },
  { label: '优惠券', value: 'COUPON' },
  { label: '店铺兑换商品', value: 'PLATFORM_LINK' },
];

const couponStatusText: Record<number | string, string> = {
  1: '未开始',
  2: '进行中',
  3: '已结束',
};

const EMPTY_HTML = '<p><br></p>';
const isEmptyHtml = (html: string) => {
  if (!html) return true;
  if (/<img\b[^>]*\bsrc\s*=\s*['"][^'"]+['"][^>]*>/i.test(html)) {
    return false;
  }
  const plain = html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, '')
    .trim();
  return !plain;
};

const ExchangeConfig: React.FC = () => {
  const [form] = Form.useForm();
  const [drawerForm] = Form.useForm();
  const { currentShopId } = useSnapshot(state);
  const memberLevelOptions = useMemo(
    () =>
      [
        { label: '一级会员', value: '1' },
        { label: '二级会员', value: '2' },
        { label: '三级会员', value: '3' },
        { label: '四级会员', value: '4' },
        { label: '五级会员', value: '5' },
      ] as { label: string; value: string }[],
    [],
  );
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [categoryTree, setCategoryTree] = useState<any[]>([]);
  const [statusOptions, setStatusOptions] = useState<{ label: string; value: any }[]>([]);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentId, setCurrentId] = useState<any>(null);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [publishTarget, setPublishTarget] = useState<any>(null);
  const [publishAdjust, setPublishAdjust] = useState<string>('');
  const [publishAssetStock, setPublishAssetStock] = useState<number | null>(null);
  const [goodsOptions, setGoodsOptions] = useState<{ label: string; value: any }[]>([]);
  const [goodsPageNum, setGoodsPageNum] = useState(1);
  const [goodsTotal, setGoodsTotal] = useState(0);
  const [goodsSearch, setGoodsSearch] = useState<string>('');
  const [goodsLoading, setGoodsLoading] = useState(false);
  const [couponOptions, setCouponOptions] = useState<{ label: string; value: any }[]>([]);
  const [couponPageNum, setCouponPageNum] = useState(1);
  const [couponTotal, setCouponTotal] = useState(0);
  const [couponSearch, setCouponSearch] = useState<string>('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [availableStock, setAvailableStock] = useState<number | null>(null);
  const [initialAssetId, setInitialAssetId] = useState<any>(null);
  const [editor, setEditor] = useState<IDomEditor | null>(null);
  const [editorHtml, setEditorHtml] = useState<string>(EMPTY_HTML);

  const normalizeAssetId = (value: any) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const resolveAssetIdFromDetail = (detail: any) =>
    detail?.productId ?? detail?.couponId ?? detail?.productNameOrId ?? detail?.productName ?? detail?.instanceName;

  const toolbarConfig = useMemo<Partial<IToolbarConfig>>(() => defaultToolbarConfig, []);

  const editorConfig = useMemo<Partial<IEditorConfig>>(() => defaultEditorConfig, []);

  useEffect(() => {
    fetchCategoryTree();
  }, []);

  useEffect(() => {
    if (currentShopId) {
      fetchStatusEnum();
      fetchList({ pageNum: 1 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentShopId]);

  useEffect(() => {
    if (!drawerVisible && editor) {
      editor.destroy();
      setEditor(null);
    }
  }, [drawerVisible, editor]);

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy();
      }
    };
  }, [editor]);

  const fetchCategoryTree = async () => {
    try {
      if (!currentShopId) return;
      const res = await getCategoryTreeApi(currentShopId);
      if (res.code === 200) {
        const rawTree = Array.isArray(res.data) ? res.data : [];
        const mapTree = (list: any[]): any[] =>
          (list || []).map(item => ({
            value: item.id,
            label: item.categoryName,
            children: item.children ? mapTree(item.children) : undefined,
          }));
        setCategoryTree(rawTree);
        setCategoryOptions(mapTree(rawTree));
        return rawTree;
      }
    } catch (error) {
      console.error(error);
    }
    return [];
  };

  const findCategoryPath = (tree: any[], targetId: number, path: number[] = []): number[] | null => {
    for (const node of tree) {
      const currentPath = [...path, Number(node.id)];
      if (Number(node.id) === Number(targetId)) {
        return currentPath;
      }
      if (node.children && node.children.length) {
        const found = findCategoryPath(node.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  };

  const mergeOptions = (oldOpts: any[], newOpts: any[]) => {
    const map = new Map<any, any>();
    [...oldOpts, ...newOpts].forEach(opt => {
      if (!map.has(opt.value)) map.set(opt.value, opt);
    });
    return Array.from(map.values());
  };

  const fetchGoodsOptions = async (keyword?: string, page = 1) => {
    if (!currentShopId) return;
    setGoodsLoading(true);
    try {
      const res = await getExchangeProductPageApi({
        shopId: currentShopId,
        productName: keyword || undefined,
        pageNum: page,
        pageSize: 20,
      });
      if (res?.code === 200) {
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        const options = rows.map((item: any) => ({
          label: item.productName || item.id,
          value: item.id,
          stock: item.productStock,
        }));
        setGoodsOptions(prev => (page === 1 ? options : mergeOptions(prev, options)));
        setGoodsPageNum(page);
        setGoodsTotal(typeof res.total === 'number' ? res.total : rows.length);
      }
    } finally {
      setGoodsLoading(false);
    }
  };

  const fetchCouponOptions = async (keyword?: string, page = 1) => {
    if (!currentShopId) return;
    setCouponLoading(true);
    try {
      const res = await queryCouponListApi({
        shopId: currentShopId,
        couponName: undefined,
        couponId: keyword || undefined,
        pageNum: page,
        pageSize: 20,
      });
      if (res?.code === 200 || res?.code === 0) {
        const rows = Array.isArray(res?.rows) ? res.rows : [];
        const options = rows.map((item: any) => ({
          label: `${item.couponName || item.couponId}-${item.couponId || item.id || ''}${
            item.status ? `（${couponStatusText[item.status] || ''}）` : ''
          }`,
          value: item.couponId || item.id,
          stock: item.num,
          status: item.status,
          disabled: item.status !== 2,
        }));
        setCouponOptions(prev => (page === 1 ? options : mergeOptions(prev, options)));
        setCouponPageNum(page);
        setCouponTotal(typeof res.total === 'number' ? res.total : rows.length);
      }
    } finally {
      setCouponLoading(false);
    }
  };

  const initialDrawerValues = useMemo(
    () => ({
      instanceType: 'PRODUCT',
      status: 'ACTIVE',
      carouselImages: [''],
      levelScope: 'all',
      memberExchangeLimit: 0,
      useProductThumbImage: 1,
      useProductCarouselImages: 1,
      detailContent: EMPTY_HTML,
    }),
    [],
  );

  const fetchStatusEnum = async () => {
    try {
      const res = await getInstanceStatusEnumApi(currentShopId);
      if (res?.code === 200) {
        const list = Array.isArray(res.data) ? res.data : [];
        setStatusOptions([...list.map((item: any) => ({ label: item.description || item.name, value: item.code }))]);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchList = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    const values = form.getFieldsValue();
    const params = {
      instanceMode: 'SHOP',
      instanceName: values.instanceName || undefined,
      productId: values.productId || undefined,
      categoryId: values.categoryId ? values.categoryId[values.categoryId.length - 1] : undefined,
      instanceType: values.instanceType || undefined,
      status: values.status || undefined,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    };
    setLoading(true);
    try {
      const res = await getInstanceListApi(currentShopId, params);
      if (res?.code === 200) {
        setDataSource(res.rows || res.data || []);
        setTotal(res.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '商品名称',
        dataIndex: 'instanceName',
        key: 'instanceName',
        width: 300,
        render: (val: string) => (
          <div style={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-all' }}>{val || '-'}</div>
        ),
      },
      { title: '商品ID', dataIndex: 'productId', key: 'productId', width: 120 },
      { title: '所属类目', dataIndex: 'categoryName', key: 'categoryName', width: 140 },
      {
        title: '资源类型',
        dataIndex: 'instanceType',
        key: 'instanceType',
        width: 120,
        render: (val: string) => {
          if (!val) return '-';
          const map: Record<string, string> = {
            PRODUCT: '实物',
            COUPON: '优惠券',
            THIRD_PARTY: '第三方兑换商品',
            PLATFORM_LINK: '店铺兑换商品',
          };
          const label = map[val] || val;
          return <Tag color={val === 'PRODUCT' ? 'processing' : 'warning'}>{label}</Tag>;
        },
      },
      { title: '兑换积分', dataIndex: 'exchangePoints', key: 'exchangePoints', width: 120 },
      {
        title: '缩略图',
        dataIndex: 'thumbImage',
        key: 'thumbImage',
        width: 120,
        render: (val: string) =>
          val ? (
            <Image
              src={val}
              width={48}
              height={48}
              preview={{ mask: null }}
              style={{ objectFit: 'cover' }}
            />
          ) : (
            <Text type="secondary">--</Text>
          ),
      },
      {
        title: '可兑数量',
        dataIndex: 'allocatedStock',
        key: 'allocatedStock',
        width: 120,
        render: (_: any, record: any) =>
          record.instanceType === 'PLATFORM_LINK' || record.instanceType === 'THIRD_PARTY'
            ? '--'
            : (record.allocatedStock ?? record.remainingStock ?? 0),
      },
      {
        title: '上架状态',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (val: any, record: any) => (
          <Switch
            checked={val === 'ACTIVE' || val === 1}
            disabled={!checkPermissions(EXCHANGE_KEY.PUBLISH)}
            onChange={checked => {
              if (!checkPermissions(EXCHANGE_KEY.PUBLISH)) return;
              if (!currentShopId) {
                message.warning('请先选择店铺');
                return;
              }
              if (!checked) {
                Modal.confirm({
                  title: '确认下架该兑换商品？',
                  onOk: async () => {
                    try {
                      const res = await unpublishInstanceApi(currentShopId, record.id);
                      if (res?.code === 200) {
                        message.success('下架成功');
                        fetchList();
                      }
                    } catch (error) {
                      console.error(error);
                    }
                  },
                });
              } else {
                (async () => {
                  try {
                    if (record.instanceType === 'PLATFORM_LINK' || record.instanceType === 'THIRD_PARTY') {
                      const res = await publishInstanceApi(
                        currentShopId,
                        record.id,
                        record.allocatedStock ?? record.availableStock ?? 0,
                      );
                      if (res?.code === 200) {
                        message.success('上架成功');
                        fetchList();
                      }
                      return;
                    }
                    if (record.instanceType === 'COUPON') {
                      const res = await getCouponDetailByCouponIdApi({
                        couponId: record.productId,
                        shopId: currentShopId,
                      });
                      if (res?.code === 200 && res.data) {
                        const assetStock = res.data.num ?? 0;
                        setPublishAssetStock(Number(assetStock) || 0);
                        setPublishTarget({
                          ...res.data,
                          ...record,
                        });
                        setPublishAdjust('');
                        setPublishModalVisible(true);
                      }
                      return;
                    }
                    const res = await getExchangeProductDetailApi(currentShopId, record.productId);
                    if (res?.code === 200 && res.data) {
                      const detail = res.data;
                      const currentProductStock = detail.productStock ?? 0;
                      setPublishAssetStock(Number(currentProductStock) || 0);
                      setPublishTarget({
                        ...res.data,
                        ...record,
                        productStock: currentProductStock,
                        frozenStock: detail.frozenStock,
                      });
                      setPublishAdjust('');
                      setPublishModalVisible(true);
                    }
                  } catch (error) {
                    console.error(error);
                  }
                })();
              }
            }}
          />
        ),
      },
      { title: '最新上/下架时间', dataIndex: 'latestStatusChangeTime', key: 'latestStatusChangeTime', width: 180 },
      { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
      {
        title: '操作',
        key: 'action',
        width: 160,
        render: (_: any, record: any) => (
          <Space size={15}>
            {checkPermissions(EXCHANGE_KEY.UPDATE) && (
              <Button
                style={{ padding: 0 }}
                type="link"
                disabled={record.status === 'ACTIVE' || record.status === 1}
                onClick={async () => {
                  if (!currentShopId) {
                    message.warning('请先选择店铺');
                    return;
                  }

                  if (record.instanceType === 'PRODUCT') {
                    await fetchGoodsOptions(record.productNameOrId);
                  } else if (record.instanceType === 'COUPON') {
                    await fetchCouponOptions(record.productId);
                  }

                  setEditMode(true);
                  setCurrentId(record.id);
                  drawerForm.resetFields();
                  drawerForm.setFieldsValue(initialDrawerValues);
                  setInitialAssetId(normalizeAssetId(null));
                  setDrawerVisible(true);
                  try {
                    const [treeData, res] = await Promise.all([
                      categoryTree.length ? Promise.resolve(categoryTree) : fetchCategoryTree(),
                      getInstanceDetailApi(currentShopId, record.id),
                    ]);
                    if (res?.code === 200 && res.data) {
                      const detail = res.data;
                      setAvailableStock(null);
                      const categoryPath =
                        detail.categoryPath && String(detail.categoryPath).length
                          ? detail.categoryPath.split('/').map((id: any) => Number(id))
                          : detail.categoryId
                            ? findCategoryPath(treeData || [], Number(detail.categoryId)) || [Number(detail.categoryId)]
                            : undefined;
                      const parsedCarousel =
                        Array.isArray(detail.carouselImages) && detail.carouselImages.length
                          ? detail.carouselImages
                          : typeof detail.carouselImages === 'string' && detail.carouselImages
                            ? (() => {
                                try {
                                  return JSON.parse(detail.carouselImages);
                                } catch (e) {
                                  return [];
                                }
                              })()
                            : [];
                      const resolvedAssetId = resolveAssetIdFromDetail(detail);
                      drawerForm.setFieldsValue({
                        ...initialDrawerValues,
                        ...detail,
                        categoryId: categoryPath,
                        productNameOrId: resolvedAssetId,
                        carouselImages: parsedCarousel.length ? parsedCarousel : [''],
                        levelScope: detail.memberLevelIds ? 'part' : 'all',
                        memberLevelIds: detail.memberLevelIds ? String(detail.memberLevelIds).split(',') : [],
                        memberExchangeLimit: detail.memberExchangeLimit && detail.memberExchangeLimit > 0 ? 1 : 0,
                        memberExchangeLimitTimes:
                          detail.memberExchangeLimit && detail.memberExchangeLimit > 0
                            ? detail.memberExchangeLimit
                            : undefined,
                        useProductThumbImage:
                          detail.instanceType === 'PRODUCT' || detail.instanceType === 'COUPON'
                            ? (detail.useProductThumbImage ?? 1)
                            : 0,
                        useProductCarouselImages:
                          detail.instanceType === 'PRODUCT' || detail.instanceType === 'COUPON'
                            ? (detail.useProductCarouselImages ?? 1)
                            : 0,
                        detailContent: detail.detailContent || EMPTY_HTML,
                      });
                      setEditorHtml(detail.detailContent || EMPTY_HTML);
                      setInitialAssetId(normalizeAssetId(resolvedAssetId));
                    }
                  } catch (error) {
                    console.error(error);
                    setDrawerVisible(false);
                  }
                }}>
                编辑
              </Button>
            )}
            <Button
              hidden
              type="link"
              style={{ padding: 0 }}
              onClick={async () => {
                if (!currentShopId) {
                  message.warning('请先选择店铺');
                  return;
                }

                if (record.instanceType === 'PRODUCT') {
                  await fetchGoodsOptions(record.productNameOrId);
                } else if (record.instanceType === 'COUPON') {
                  await fetchCouponOptions(record.productId);
                }

                setEditMode(false);
                setCurrentId(null);
                drawerForm.resetFields();
                drawerForm.setFieldsValue(initialDrawerValues);
                setInitialAssetId(normalizeAssetId(null));
                setDrawerVisible(true);
                try {
                  const res = await getInstanceDetailApi(currentShopId, record.id);
                  if (res?.code === 200 && res.data) {
                    const detail = res.data;
                    setAvailableStock(null);
                    const categoryPath =
                      detail.categoryPath && String(detail.categoryPath).length
                        ? detail.categoryPath.split('/').map((id: any) => Number(id))
                        : detail.categoryId
                          ? findCategoryPath(categoryTree, Number(detail.categoryId)) || [Number(detail.categoryId)]
                          : undefined;
                    const parsedCarousel =
                      Array.isArray(detail.carouselImages) && detail.carouselImages.length
                        ? detail.carouselImages
                        : typeof detail.carouselImages === 'string' && detail.carouselImages
                          ? (() => {
                              try {
                                return JSON.parse(detail.carouselImages);
                              } catch (e) {
                                return [];
                              }
                            })()
                          : [];
                    const resolvedAssetId = resolveAssetIdFromDetail(detail);
                    drawerForm.setFieldsValue({
                      ...initialDrawerValues,
                      ...detail,
                      categoryId: categoryPath,
                      productNameOrId: resolvedAssetId,
                      carouselImages: parsedCarousel.length ? parsedCarousel : [''],
                      levelScope: detail.memberLevelIds ? 'part' : 'all',
                      memberLevelIds: detail.memberLevelIds ? String(detail.memberLevelIds).split(',') : [],
                      memberExchangeLimit: detail.memberExchangeLimit && detail.memberExchangeLimit > 0 ? 1 : 0,
                      memberExchangeLimitTimes:
                        detail.memberExchangeLimit && detail.memberExchangeLimit > 0
                          ? detail.memberExchangeLimit
                          : undefined,
                      useProductThumbImage:
                        detail.instanceType === 'PRODUCT' || detail.instanceType === 'COUPON'
                          ? (detail.useProductThumbImage ?? 1)
                          : 0,
                      useProductCarouselImages:
                        detail.instanceType === 'PRODUCT' || detail.instanceType === 'COUPON'
                          ? (detail.useProductCarouselImages ?? 1)
                          : 0,
                      detailContent: detail.detailContent || EMPTY_HTML,
                    });
                    setEditorHtml(detail.detailContent || EMPTY_HTML);
                  }
                } catch (error) {
                  console.error(error);
                  setDrawerVisible(false);
                }
              }}>
              复制
            </Button>
            {checkPermissions(EXCHANGE_KEY.DELETE) && (
              <Button
                type="link"
                style={{ padding: 0 }}
                danger
                onClick={() => {
                  if (!currentShopId) {
                    message.warning('请先选择店铺');
                    return;
                  }
                  Modal.confirm({
                    title: '确认删除该兑换商品？',
                    onOk: async () => {
                      try {
                        const res = await deleteInstanceApi(currentShopId, record.id);
                        if (res?.code === 200) {
                          message.success('删除成功');
                          fetchList();
                        }
                      } catch (error) {
                        console.error(error);
                      }
                    },
                  });
                }}>
                删除
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [],
  );

  return (
    <div style={{ padding: 24 }}>
      <Form
        form={form}
        layout="inline"
        style={{ marginBottom: 16, rowGap: 12 }}>
        <Form.Item
          label="商品名称"
          name="instanceName">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="商品ID"
          name="productId">
          <Input
            placeholder="请输入"
            style={{ width: 200 }}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="所属类目"
          name="categoryId">
          <Cascader
            placeholder="全部"
            allowClear
            style={{ width: 220 }}
            options={categoryOptions}
          />
        </Form.Item>
        <Form.Item
          label="资源类型"
          name="instanceType">
          <Select
            placeholder="全部"
            style={{ width: 180 }}
            options={instanceTypeOptions}
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="上下架状态"
          name="status">
          <Select
            placeholder="全部"
            style={{ width: 180 }}
            options={statusOptions}
            allowClear
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => fetchList({ pageNum: 1 })}>
              查询
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                form.resetFields();
                fetchList({ pageNum: 1 });
              }}>
              重置
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <div style={{ marginBottom: 16 }}>
        {checkPermissions(EXCHANGE_KEY.ADD) && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              drawerForm.resetFields();
              drawerForm.setFieldsValue(initialDrawerValues);
              setEditMode(false);
              setCurrentId(null);
              setInitialAssetId(normalizeAssetId(null));
              setEditorHtml(EMPTY_HTML);
              setDrawerVisible(true);
            }}>
            新建商品
          </Button>
        )}
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        bordered
        pagination={{
          current: pageNum,
          pageSize,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, size) => fetchList({ pageNum: page, pageSize: size }),
        }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title="调整可兑数量"
        open={publishModalVisible}
        onCancel={() => setPublishModalVisible(false)}
        onOk={async () => {
          if (!currentShopId || !publishTarget?.id) {
            setPublishModalVisible(false);
            return;
          }
          const remaining =
            publishTarget?.instanceType === 'COUPON'
              ? Number(publishTarget?.num ?? publishAssetStock ?? 0)
              : Number(publishAssetStock ?? 0);
          const allocatedStock = Number(publishAdjust);
          if (Number.isNaN(allocatedStock)) {
            message.error('请输入可兑数量');
            return;
          }
          if (allocatedStock < 0) {
            message.error('可兑数量不能为负数');
            return;
          }
          if (allocatedStock > remaining) {
            message.error('可兑数量不能大于资产可分配库存');
            return;
          }
          try {
            const res = await publishInstanceApi(currentShopId, publishTarget.id, allocatedStock);
            if (res?.code === 200) {
              message.success('上架成功');
              setPublishModalVisible(false);
              fetchList();
            }
          } catch (error) {
            console.error(error);
          }
        }}
        okText="提交">
        <div style={{ marginBottom: 8 }}>资产可分配库存：{publishAssetStock ?? '--'}</div>
        <div style={{ marginBottom: 8 }}>当前可兑数量： 0</div>
        <div style={{ marginBottom: 8 }}>
          <span style={{ color: '#f5222d' }}>*</span> 追加可兑数量：
          <Input
            style={{ marginTop: 8 }}
            placeholder="请输入"
            value={publishAdjust}
            onChange={e => setPublishAdjust(e.target.value)}
          />
        </div>
        <div style={{ color: '#999', fontSize: 12, lineHeight: '18px' }}>
          注：追加可兑数量不能大于资产可分配数量
          <br />
          如需扣减可兑数量，可填写负数，扣减可兑数量不可大于当前可兑数量
        </div>
      </Modal>

      <Drawer
        title={editMode ? '编辑积分兑换商品' : '新增积分兑换商品'}
        width={900}
        open={drawerVisible}
        destroyOnHidden
        onClose={() => setDrawerVisible(false)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setDrawerVisible(false)}>取消</Button>
              <Button
                type="primary"
                loading={submitLoading}
                onClick={async () => {
                  if (!currentShopId) {
                    message.warning('请先选择店铺');
                    return;
                  }
                  try {
                    drawerForm.setFieldsValue({ detailContent: editorHtml });
                    const values = await drawerForm.validateFields();
                    const type = values.instanceType;
                    const resolvedProductId =
                      type === 'PRODUCT' || type === 'COUPON' ? (values.productNameOrId ?? undefined) : undefined;
                    const normalizeImages = (images: any[] = []) => (images || []).filter((i: string) => i);
                    const useCustomCarousel =
                      type === 'THIRD_PARTY'
                        ? true
                        : ['PRODUCT', 'COUPON'].includes(type)
                          ? Number(values.useProductCarouselImages) === 0
                          : false;
                    const carouselImages =
                      useCustomCarousel || type === 'THIRD_PARTY'
                        ? normalizeImages(values.carouselImages || [])
                        : undefined;
                    if (useCustomCarousel && (!carouselImages || !carouselImages.length)) {
                      message.error('请上传轮播图');
                      return;
                    }

                    const payload: any = {
                      ...values,
                      shopId: currentShopId,
                      categoryId: values.categoryId ? values.categoryId[values.categoryId.length - 1] : undefined,
                      productNameOrId: values.productNameOrId || undefined,
                      productId: resolvedProductId,
                      carouselImages,
                      memberLevelIds:
                        values.levelScope === 'part' ? (values.memberLevelIds || []).join(',') : undefined,
                      useProductThumbImage:
                        values.instanceType === 'PRODUCT' || values.instanceType === 'COUPON'
                          ? Number(values.useProductThumbImage)
                          : 0,
                      useProductCarouselImages:
                        values.instanceType === 'PRODUCT' || values.instanceType === 'COUPON'
                          ? Number(values.useProductCarouselImages)
                          : 0,
                      memberExchangeLimit:
                        values.memberExchangeLimit === 1 ? Number(values.memberExchangeLimitTimes || 0) : 0,
                    };
                    if (values.instanceType === 'THIRD_PARTY') {
                      if (isEmptyHtml(editorHtml)) {
                        message.error('请输入商品详情');
                        return;
                      }
                      payload.detailContent = editorHtml;
                    }
                    if (type === 'PRODUCT' || type === 'COUPON') {
                      if (payload.allocatedStock !== undefined && payload.allocatedStock !== null) {
                        const stockNumber = Number(payload.allocatedStock);
                        if (Number.isNaN(stockNumber)) {
                          message.error('可兑数量需为数字');
                          return;
                        }
                        payload.allocatedStock = stockNumber;
                      }
                      if (
                        payload.status === 'ACTIVE' &&
                        (payload.allocatedStock === undefined || payload.allocatedStock === null)
                      ) {
                        message.error('上架状态下需填写可兑换数量');
                        return;
                      }
                    } else {
                      payload.allocatedStock = undefined;
                    }
                    if (payload.instanceType !== 'PRODUCT' && payload.instanceType !== 'COUPON') {
                      payload.useProductThumbImage = 0;
                      payload.useProductCarouselImages = 0;
                    }
                    if (payload.instanceType !== 'THIRD_PARTY' && payload.instanceType !== 'PLATFORM_LINK') {
                      payload.thirdPartyUrl = undefined;
                      payload.thirdPartyParams = undefined;
                      payload.detailContent = undefined;
                      payload.strikePrice = undefined;
                      payload.price = undefined;
                    }
                    if (payload.useProductThumbImage === 1) {
                      payload.thumbImage = undefined;
                    }
                    setSubmitLoading(true);
                    const api = editMode ? updateInstanceApi : addInstanceApi;
                    const res = await api(currentShopId, editMode ? { ...payload, id: currentId } : payload);
                    if (res?.code === 200) {
                      message.success(editMode ? '编辑成功' : '新增成功');
                      setDrawerVisible(false);
                      fetchList({ pageNum: 1 });
                    }
                  } catch (error: any) {
                    if (error?.errorFields) return;
                    console.error(error);
                  } finally {
                    setSubmitLoading(false);
                  }
                }}>
                保存
              </Button>
            </Space>
          </div>
        }>
        <Form
          form={drawerForm}
          layout="horizontal"
          labelCol={{ style: { width: 140 } }}
          wrapperCol={{ style: { flex: 1 } }}
          initialValues={initialDrawerValues}
          onValuesChange={(_, allValues) => {
            // 打印当前将要提交的核心字段，便于调试
            console.log('兑换商品表单变更:', allValues);
            if (editMode && allValues.instanceType === 'PRODUCT') {
              setAvailableStock(null);
            }
          }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>选择资产项</div>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const showThirdParty = editMode && getFieldValue('instanceType') === 'THIRD_PARTY';
              return (
                <Form.Item
                  label="商品类型"
                  name="instanceType"
                  rules={[{ required: true, message: '请选择商品类型' }]}>
                  <Radio.Group
                    disabled={editMode}
                    onChange={e => {
                      const nextType = e.target.value;
                      const currentStatus = drawerForm.getFieldValue('status');
                      drawerForm.resetFields();
                      drawerForm.setFieldsValue({
                        ...initialDrawerValues,
                        instanceType: nextType,
                        status: currentStatus ?? initialDrawerValues.status,
                        useProductThumbImage:
                          nextType === 'PLATFORM_LINK' || nextType === 'THIRD_PARTY'
                            ? 0
                            : initialDrawerValues.useProductThumbImage,
                        useProductCarouselImages:
                          nextType === 'PLATFORM_LINK' || nextType === 'THIRD_PARTY'
                            ? 0
                            : initialDrawerValues.useProductCarouselImages,
                      });
                      setEditorHtml(EMPTY_HTML);
                      setAvailableStock(null);
                    }}>
                    <Radio value="PRODUCT">实物</Radio>
                    <Radio value="COUPON">店铺优惠券</Radio>
                    {showThirdParty ? <Radio value="THIRD_PARTY">第三方平台兑换商品</Radio> : null}
                    <Radio value="PLATFORM_LINK">店铺兑换商品</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const type = getFieldValue('instanceType');
              return (
                <>
                  {(type === 'THIRD_PARTY' || type === 'PLATFORM_LINK') && (
                    <Form.Item
                      label={type === 'THIRD_PARTY' ? '三方平台链接' : '积分兑换链接'}
                      name="thirdPartyUrl"
                      rules={[{ required: true, message: '请输入兑换链接' }]}>
                      <Input placeholder="请输入" />
                    </Form.Item>
                  )}
                  {type !== 'PLATFORM_LINK' && type !== 'THIRD_PARTY' ? (
                    <Form.Item
                      label={type === 'COUPON' ? '优惠券名称或ID' : '选择兑换资产'}
                      name="productNameOrId"
                      rules={[
                        {
                          required: type === 'PRODUCT' || type === 'COUPON',
                          message: type === 'COUPON' ? '请选择优惠券' : '请选择兑换资产',
                        },
                      ]}>
                      {type === 'PRODUCT' ? (
                        <Select
                          showSearch
                          placeholder="请选择兑换资产"
                          options={goodsOptions}
                          loading={goodsLoading}
                          filterOption={false}
                          onFocus={() => {
                            setGoodsSearch('');
                            setAvailableStock(null);
                            fetchGoodsOptions();
                          }}
                          onSearch={value => {
                            setGoodsSearch(value);
                            fetchGoodsOptions(value);
                          }}
                          onChange={(_, option: any) => {
                            setAvailableStock(typeof option?.stock === 'number' ? option.stock : null);
                          }}
                          onPopupScroll={e => {
                            const target = e.target as HTMLDivElement;
                            if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
                              const hasMore = goodsOptions.length < goodsTotal;
                              if (!goodsLoading && hasMore) {
                                fetchGoodsOptions(goodsSearch, goodsPageNum + 1);
                              }
                            }
                          }}
                        />
                      ) : type === 'COUPON' ? (
                        <Select
                          showSearch
                          placeholder="请选择优惠券"
                          options={couponOptions}
                          loading={couponLoading}
                          filterOption={(input, option) => {
                            if (!option?.label) return false;
                            const label = String(option.label).toLowerCase();
                            return label.includes(input.toLowerCase());
                          }}
                          onFocus={() => {
                            setCouponSearch('');
                            setAvailableStock(null);
                            fetchCouponOptions();
                          }}
                          onSearch={value => {
                            setCouponSearch(value);
                          }}
                          onChange={(_, option: any) => {
                            setAvailableStock(typeof option?.stock === 'number' ? option.stock : null);
                          }}
                          onPopupScroll={e => {
                            const target = e.target as HTMLDivElement;
                            if (target.scrollTop + target.clientHeight >= target.scrollHeight - 16) {
                              const hasMore = couponOptions.length < couponTotal;
                              if (!couponLoading && hasMore) {
                                fetchCouponOptions(couponSearch, couponPageNum + 1);
                              }
                            }
                          }}
                        />
                      ) : (
                        <Input
                          placeholder={
                            type === 'COUPON' ? '请输入优惠券名称或ID' : '请输入兑换资产名称或ID（第三方可选）'
                          }
                        />
                      )}
                    </Form.Item>
                  ) : null}
                </>
              );
            }}
          </Form.Item>

          <div style={{ fontWeight: 600, marginBottom: 8 }}>商品信息</div>
          <Form.Item
            label="积分兑换商品名称"
            name="instanceName"
            rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="请输入" />
          </Form.Item>
          <Form.Item
            label="所属类目"
            name="categoryId"
            rules={[{ required: true, message: '请选择类目' }]}>
            <Cascader
              placeholder="请选择"
              options={categoryOptions}
            />
          </Form.Item>
          <Form.Item
            label="兑换等级限制"
            name="levelScope"
            initialValue="all">
            <Radio.Group>
              <Radio value="all">全部等级</Radio>
              <Radio value="part">部分等级</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) =>
              getFieldValue('levelScope') === 'part' ? (
                <Form.Item
                  label="选择会员等级"
                  name="memberLevelIds"
                  rules={[{ required: true, message: '请选择会员等级' }]}>
                  <Checkbox.Group options={memberLevelOptions} />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item
            label="兑换所需积分"
            name="exchangePoints"
            rules={[{ required: true, message: '请输入兑换所需积分' }]}>
            <InputNumber
              placeholder="请输入"
              style={{ width: 200 }}
              min={1}
              max={99999999}
              precision={0}
              stringMode
            />
          </Form.Item>
          <Form.Item
            label="积分划线价"
            name="linePoints">
            <InputNumber
              placeholder="请输入"
              style={{ width: 200 }}
              min={0}
              max={99999999}
              precision={0}
              stringMode
            />
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const type = getFieldValue('instanceType');
              if (type !== 'PLATFORM_LINK' && type !== 'THIRD_PARTY') return null;
              return (
                <>
                  <Form.Item
                    label="划线金额"
                    name="strikePrice">
                    <InputNumber
                      placeholder="请输入"
                      style={{ width: 200 }}
                      min={0}
                      max={99999999}
                      precision={2}
                      stringMode
                    />
                  </Form.Item>
                  <Form.Item
                    label="金额"
                    name="price">
                    <InputNumber
                      placeholder="请输入"
                      style={{ width: 200 }}
                      min={0}
                      max={99999999}
                      precision={2}
                      stringMode
                    />
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const type = getFieldValue('instanceType');
              if (type === 'PLATFORM_LINK' || type === 'THIRD_PARTY') return null;
              return (
                <>
                  <Form.Item
                    label="单个会员兑换限制"
                    name="memberExchangeLimit">
                    <Radio.Group>
                      <Radio value={0}>不限制</Radio>
                      <Radio value={1}>每人限兑</Radio>
                    </Radio.Group>
                  </Form.Item>
                  <Form.Item
                    shouldUpdate
                    noStyle>
                    {({ getFieldValue: innerGet }) =>
                      innerGet('memberExchangeLimit') === 1 ? (
                        <Form.Item
                          name="memberExchangeLimitTimes"
                          label="次数"
                          rules={[{ required: true, message: '请输入限制次数' }]}>
                          <InputNumber
                            style={{ width: 200 }}
                            placeholder="请输入次数"
                            min={1}
                            max={999999}
                            precision={0}
                            stringMode
                          />
                        </Form.Item>
                      ) : null
                    }
                  </Form.Item>
                </>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const type = getFieldValue('instanceType');
              const assetId = normalizeAssetId(getFieldValue('productNameOrId'));
              const status = getFieldValue('status');
              const isActive = status === 'ACTIVE';
              const showAllocatedStock = isActive || !editMode || assetId !== initialAssetId;
              const maxStock = typeof availableStock === 'number' ? availableStock : undefined;
              if (type === 'PLATFORM_LINK' || type === 'THIRD_PARTY') return null;
              return showAllocatedStock ? (
                <>
                  <Form.Item
                    label="可兑数量"
                    name="allocatedStock">
                    <InputNumber
                      style={{ width: 200, marginRight: 8 }}
                      placeholder="请输入"
                      min={1}
                      max={maxStock}
                      precision={0}
                      stringMode
                    />
                  </Form.Item>
                  {type === 'PRODUCT' && maxStock !== undefined && (
                    <div style={{ marginLeft: 140, marginTop: -12, marginBottom: 12 }}>
                      实物资产可分配库存：{maxStock}{' '}
                      <span style={{ color: '#999' }}>注：兑换数量不可大于实物资产可分配库存</span>
                    </div>
                  )}
                  {type === 'COUPON' && maxStock !== undefined && (
                    <div style={{ marginLeft: 140, marginTop: -12, marginBottom: 12 }}>
                      优惠券可分配数量：{maxStock}{' '}
                      <span style={{ color: '#999' }}>注：兑换数量不可大于优惠券可分配数量</span>
                    </div>
                  )}
                </>
              ) : null;
            }}
          </Form.Item>
          <Form.Item
            label="上下架状态"
            name="status"
            rules={[{ required: true, message: '请选择状态' }]}>
            <Radio.Group>
              <Radio value="ACTIVE">上架</Radio>
              <Radio value="INACTIVE">下架</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const isProductOrCoupon = ['PRODUCT', 'COUPON'].includes(getFieldValue('instanceType'));
              const isPlatformLink = getFieldValue('instanceType') === 'PLATFORM_LINK';
              const isThirdParty = getFieldValue('instanceType') === 'THIRD_PARTY';
              if (isPlatformLink || isThirdParty) {
                return (
                  <Form.Item
                    label="缩略图"
                    name="thumbImage"
                    required
                    rules={[{ required: true, message: '请上传缩略图' }]}>
                    <ImageUploader />
                  </Form.Item>
                );
              }
              return (
                <Form.Item
                  label="缩略图"
                  name="useProductThumbImage"
                  required>
                  <Radio.Group>
                    <Radio
                      value={1}
                      disabled={!isProductOrCoupon}>
                      自动同步资产项
                    </Radio>
                    <Radio value={0}>自定义</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) =>
              getFieldValue('useProductThumbImage') === 0 &&
              getFieldValue('instanceType') !== 'PLATFORM_LINK' &&
              getFieldValue('instanceType') !== 'THIRD_PARTY' ? (
                <Form.Item
                  name="thumbImage"
                  style={{ paddingLeft: 140 }}
                  rules={[{ required: true, message: '请上传缩略图' }]}>
                  <ImageUploader />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) => {
              const isProductOrCoupon = ['PRODUCT', 'COUPON'].includes(getFieldValue('instanceType'));
              const currentType = getFieldValue('instanceType');
              if (currentType === 'PLATFORM_LINK') return null;
              if (currentType === 'THIRD_PARTY') {
                return (
                  <Form.Item
                    label="轮播图"
                    required>
                    <Form.List name="carouselImages">
                      {(fields, { add, remove }) => {
                        const canAdd = fields.length < 5;
                        return (
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 16,
                              alignItems: 'flex-start',
                            }}>
                            {fields.map(field => (
                              <div
                                key={field.key}
                                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                                <Form.Item
                                  {...field}
                                  name={[field.name]}
                                  rules={[{ required: false }]}
                                  style={{ marginBottom: 0 }}>
                                  <ImageUploader />
                                </Form.Item>
                                {fields.length > 1 && (
                                  <Button
                                    type="link"
                                    danger
                                    onClick={() => remove(field.name)}>
                                    删除
                                  </Button>
                                )}
                              </div>
                            ))}
                            {canAdd && (
                              <Button
                                type="dashed"
                                style={{
                                  width: 80,
                                  height: 80,
                                }}
                                onClick={() => add('')}
                                disabled={fields.length >= 5}>
                                新增轮播图
                              </Button>
                            )}
                          </div>
                        );
                      }}
                    </Form.List>
                  </Form.Item>
                );
              }
              return (
                <Form.Item
                  label="轮播图"
                  name="useProductCarouselImages"
                  required>
                  <Radio.Group>
                    <Radio
                      value={1}
                      disabled={!isProductOrCoupon}>
                      自动同步资产项
                    </Radio>
                    <Radio value={0}>自定义</Radio>
                  </Radio.Group>
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) =>
              getFieldValue('useProductCarouselImages') === 0 &&
              getFieldValue('instanceType') !== 'PLATFORM_LINK' &&
              getFieldValue('instanceType') !== 'THIRD_PARTY' ? (
                <Form.List name="carouselImages">
                  {(fields, { add, remove }) => {
                    const canAdd = fields.length < 5;
                    return (
                      <div style={{ paddingLeft: 140 }}>
                        <div
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 16,
                            alignItems: 'flex-start',
                          }}>
                          {fields.map(field => (
                            <div
                              key={field.key}
                              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                              <Form.Item
                                {...field}
                                name={[field.name]}
                                rules={[{ required: false }]}
                                style={{ marginBottom: 0 }}>
                                <ImageUploader />
                              </Form.Item>
                              {fields.length > 1 && (
                                <Button
                                  type="link"
                                  danger
                                  onClick={() => remove(field.name)}>
                                  删除
                                </Button>
                              )}
                            </div>
                          ))}
                          {canAdd && (
                            <Button
                              type="dashed"
                              style={{
                                width: 80,
                                height: 80,
                              }}
                              onClick={() => add('')}
                              disabled={fields.length >= 5}>
                              新增轮播图
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Form.List>
              ) : null
            }
          </Form.Item>
          <Form.Item
            shouldUpdate
            noStyle>
            {({ getFieldValue }) =>
              getFieldValue('instanceType') === 'THIRD_PARTY' ? (
                <Form.Item
                  label="商品详情"
                  name="detailContent"
                  rules={[
                    {
                      validator: async () => {
                        if (isEmptyHtml(editorHtml)) {
                          return Promise.reject(new Error('请输入商品详情'));
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}>
                  {drawerVisible ? (
                    <div style={{ border: '1px solid #f0f0f0', borderRadius: 4 }}>
                      <Toolbar
                        editor={editor}
                        defaultConfig={toolbarConfig}
                        style={{ borderBottom: '1px solid #f0f0f0' }}
                      />
                      <Editor
                        key={drawerVisible ? 'editor-open' : 'editor-closed'}
                        defaultConfig={editorConfig}
                        value={editorHtml}
                        onCreated={setEditor}
                        onChange={ed => {
                          const html = ed.getHtml();
                          setEditorHtml(html);
                          drawerForm.setFieldsValue({ detailContent: html });
                        }}
                        style={{ minHeight: 220, maxHeight: 400, overflowY: 'auto', padding: 12 }}
                      />
                    </div>
                  ) : null}
                </Form.Item>
              ) : null
            }
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default ExchangeConfig;
